import React from 'react';
import * as R from 'ramda';
import * as Yup from 'yup';
// features
import { AuthWrapper } from '../../permission';
import PC from '../../permission/role-permission';
// forms
import { Toggle } from '../../../forms';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import { ENUMS } from '../../../constants/enums';
// icons
import * as I from '../../../svgs';
// ui
import { Box, Flex } from '../../../ui';
// utilities
import endpointsMap from '../../../utilities/endpoints';
// feature fleet-profile
import { TableCell } from '../components/table-cells';
import AssignmentsIntegrations from '../components/assignments-integrations';
//////////////////////////////////////////////////

const eldIntegrationTypes = [
  GC.FUEL_CARDS_TYPE_RELAY,
  GC.FLEET_INTEGRATION_SPIREON,
  GC.FLEET_INTEGRATION_ORBCOMM,
  GC.GPS_INTEGRATION_TYPE_ELD_88,
  GC.GPS_INTEGRATION_TYPE_GEO_TAB,
  GC.GPS_INTEGRATION_TYPE_VERIZON,
  GC.GPS_INTEGRATION_TYPE_HGRS_ELD,
  GC.GPS_INTEGRATION_TYPE_CLUB_ELD,
  GC.FLEET_INTEGRATION_TYPE_SAMSARA,
  GC.FLEET_INTEGRATION_TYPE_SKYBITZ,
  GC.GPS_INTEGRATION_TYPE_OMNITRACS,
  GC.GPS_INTEGRATION_TYPE_BLUE_STAR,
  GC.FLEET_INTEGRATION_TYPE_ANYTREK,
  GC.GPS_INTEGRATION_TYPE_LUCID_ELD,
  GC.FLEET_INTEGRATION_TYPE_SAREKON,
  GC.GPS_INTEGRATION_TYPE_FTSGPS_ELD,
  GC.FLEET_INTEGRATION_GPS_AND_FLEET,
  GC.GPS_INTEGRATION_TYPE_MASTER_ELD,
  GC.GPS_INTEGRATION_TYPE_XPLORE_ELD,
  GC.FLEET_INTEGRATION_TYPE_PHILLIPS,
  GC.GPS_INTEGRATION_TYPE_SPARKLE_ELD,
  GC.GPS_INTEGRATION_TYPE_MAESTRAL_ELD,
  GC.GPS_INTEGRATION_TYPE_TRACK_ENSURE,
  GC.FLEET_INTEGRATION_TYPE_XTRA_LEASE,
  GC.FLEET_INTEGRATION_TYPE_POWER_FLEET,
  GC.GPS_INTEGRATION_COUNTING_TRUCKS_ELD,
  GC.FLEET_INTEGRATION_TYPE_KEEP_TRUCKIN,
  GC.REPAIR_SHOP_INTEGRATION_TYPE_FULLBAY,
  GC.FLEET_INTEGRATION_TYPE_EQUIPMENT_SHARE,
  GC.DRIVER_ONBOARDING_INTEGRATION_TYPE_TENSTREET,
];

const fuelCardTypes = [
  GC.FUEL_CARDS_TYPE_BVD,
  GC.FUEL_CARDS_TYPE_QUIKQ,
  GC.FUEL_CARDS_TYPE_COMDATA,
  GC.FUEL_CARDS_TYPE_COMPASS,
  GC.FLEET_INTEGRATION_TYPE_EFS,
  GC.FUEL_CARDS_TYPE_SELF_FUELED,
];

const tollChargeTypes = [
  GC.FLEET_INTEGRATION_TYPE_I_PASS,
  GC.FLEET_INTEGRATION_TYPE_BESTPASS,
  GC.FLEET_INTEGRATION_TYPE_PRE_PASS,
  GC.FLEET_INTEGRATION_TYPE_SELF_TOLL,
  GC.FLEET_INTEGRATION_TYPE_BESTPASS_HORIZON,
];

const accountingIntegrationTypes = [
  GC.DRIVER_ACCOUNTING_INTEGRATION_TYPE_PROPEL_HR,
  GC.DRIVER_ACCOUNTING_INTEGRATION_TYPE_PAYLOCITY,
];

const eldOptions = [
  {
    label: 'Blue Star',
    value: GC.GPS_INTEGRATION_TYPE_BLUE_STAR,
  },
  {
    label: 'Club ELD',
    value: GC.GPS_INTEGRATION_TYPE_CLUB_ELD,
  },
  {
    label: 'Counting Trucks ELD',
    value: GC.GPS_INTEGRATION_COUNTING_TRUCKS_ELD,
  },
  {
    label: 'ELD 88',
    value: GC.GPS_INTEGRATION_TYPE_ELD_88,
  },
  {
    label: 'FTSGPS ELD',
    value: GC.GPS_INTEGRATION_TYPE_FTSGPS_ELD,
  },
  {
    label: 'GEOTAB',
    value: GC.GPS_INTEGRATION_TYPE_GEO_TAB,
  },
  {
    label: 'HGRS ELD',
    value: GC.GPS_INTEGRATION_TYPE_HGRS_ELD,
  },
  {
    label: 'Motive',
    value: GC.FLEET_INTEGRATION_TYPE_KEEP_TRUCKIN,
  },
  {
    label: 'Lucid ELD',
    value: GC.GPS_INTEGRATION_TYPE_LUCID_ELD,
  },
  {
    label: 'Maestral ELD',
    value: GC.GPS_INTEGRATION_TYPE_MAESTRAL_ELD,
  },
  {
    label: 'Master ELD',
    value: GC.GPS_INTEGRATION_TYPE_MASTER_ELD,
  },
  {
    label: 'Omnitracs',
    value: GC.GPS_INTEGRATION_TYPE_OMNITRACS,
  },
  {
    label: 'Samsara',
    value: GC.FLEET_INTEGRATION_TYPE_SAMSARA,
  },
  {
    label: 'Sparkle ELD',
    value: GC.GPS_INTEGRATION_TYPE_SPARKLE_ELD,
  },
  {
    label: 'Track Ensure',
    value: GC.GPS_INTEGRATION_TYPE_TRACK_ENSURE,
  },
  {
    label: 'Verizon',
    value: GC.GPS_INTEGRATION_TYPE_VERIZON,
  },
  {
    label: 'Xplore ELD',
    value: GC.GPS_INTEGRATION_TYPE_XPLORE_ELD,
  },
];

const driverEldOptions = R.reject(R.propEq(GC.GPS_INTEGRATION_TYPE_FTSGPS_ELD, GC.FIELD_VALUE), eldOptions);

const fuelCardOptions = [
  {
    label: 'BVD Group',
    value: GC.FUEL_CARDS_TYPE_BVD,
  },
  {
    value: GC.FUEL_CARDS_TYPE_COMDATA,
    label: G.getWindowLocale('titles:comdata', 'Comdata'),
  },
  {
    label: 'Compass',
    value: GC.FUEL_CARDS_TYPE_COMPASS,
  },
  {
    label: 'EFS',
    value: GC.FLEET_INTEGRATION_TYPE_EFS,
  },
  {
    label: 'Relay',
    value: GC.FUEL_CARDS_TYPE_RELAY,
  },
  {
    label: 'Self Fuel',
    value: GC.FUEL_CARDS_TYPE_SELF_FUELED,
  },
  {
    label: 'QUIKQ',
    value: GC.FUEL_CARDS_TYPE_QUIKQ,
  },
];

const onboardingOptions = [
  {
    label: 'Tenstreet',
    value: GC.DRIVER_ONBOARDING_INTEGRATION_TYPE_TENSTREET,
  },
];

const repairShopOptions = [
  {
    label: 'Fullbay',
    value: GC.REPAIR_SHOP_INTEGRATION_TYPE_FULLBAY,
  },
];

const tollChargeOptions = [
  {
    label: 'Bestpass',
    value: GC.FLEET_INTEGRATION_TYPE_BESTPASS,
  },
  {
    label: 'Bestpass Horizon',
    value: GC.FLEET_INTEGRATION_TYPE_BESTPASS_HORIZON,
  },
  {
    label: 'I-Pass',
    value: GC.FLEET_INTEGRATION_TYPE_I_PASS,
  },
  {
    label: 'PrePass',
    value: GC.FLEET_INTEGRATION_TYPE_PRE_PASS,
  },
  {
    label: 'Self Toll',
    value: GC.FLEET_INTEGRATION_TYPE_SELF_TOLL,
  },
];

const accountingOptions = [
  {
    label: 'Propel HR',
    value: GC.DRIVER_ACCOUNTING_INTEGRATION_TYPE_PROPEL_HR,
  },
  {
    label: 'Paylocity',
    value: GC.DRIVER_ACCOUNTING_INTEGRATION_TYPE_PAYLOCITY,
  },
];

const driverIntegrationTypeOptions = [
  {
    options: driverEldOptions,
    label: G.getWindowLocale('titles:eld', 'ELD'),
  },
  {
    options: fuelCardOptions,
    label: G.getWindowLocale('titles:fuel-cards', 'Fuel Cards'),
  },
  {
    options: onboardingOptions,
    label: G.getWindowLocale('titles:onboarding', 'Onboarding'),
  },
  {
    options: tollChargeOptions,
    label: G.getWindowLocale('titles:toll-charges', 'Toll Charges'),
  },
  {
    options: accountingOptions,
    label: G.getWindowLocale('titles:accounting', 'Accounting'),
  },
];

const truckIntegrationTypeOptions = [
  {
    options: eldOptions,
    label: G.getWindowLocale('titles:eld', 'ELD'),
  },
  {
    options: tollChargeOptions,
    label: G.getWindowLocale('titles:toll-charges', 'Toll Charges'),
  },
  {
    options: repairShopOptions,
    label: G.getWindowLocale('titles:repair-shop', 'Repair Shop'),
  },
];

const trailerEldOptions = [
  {
    label: 'Anytrek',
    value: GC.FLEET_INTEGRATION_TYPE_ANYTREK,
  },
  {
    label: 'Equipment Share',
    value: GC.FLEET_INTEGRATION_TYPE_EQUIPMENT_SHARE,
  },
  {
    label: 'FTSGPS ELD',
    value: GC.GPS_INTEGRATION_TYPE_FTSGPS_ELD,
  },
  {
    label: 'GPS and Fleet',
    value: GC.FLEET_INTEGRATION_GPS_AND_FLEET,
  },
  {
    label: 'Motive',
    value: GC.FLEET_INTEGRATION_TYPE_KEEP_TRUCKIN,
  },
  {
    label: 'Maestral ELD',
    value: GC.GPS_INTEGRATION_TYPE_MAESTRAL_ELD,
  },
  {
    label: 'Orbcomm',
    value: GC.FLEET_INTEGRATION_ORBCOMM,
  },
  {
    label: 'Phillips',
    value: GC.FLEET_INTEGRATION_TYPE_PHILLIPS,
  },
  {
    label: 'Power Fleet',
    value: GC.FLEET_INTEGRATION_TYPE_POWER_FLEET,
  },
  {
    label: 'Samsara',
    value: GC.FLEET_INTEGRATION_TYPE_SAMSARA,
  },
  {
    label: 'Sarekon',
    value: GC.FLEET_INTEGRATION_TYPE_SAREKON,
  },
  {
    label: 'Skybitz',
    value: GC.FLEET_INTEGRATION_TYPE_SKYBITZ,
  },
  {
    label: 'Sparkle ELD',
    value: GC.GPS_INTEGRATION_TYPE_SPARKLE_ELD,
  },
  {
    label: 'Spireon',
    value: GC.FLEET_INTEGRATION_SPIREON,
  },
  {
    label: 'Xtra Lease',
    value: GC.FLEET_INTEGRATION_TYPE_XTRA_LEASE,
  },
];

const trailerIntegrationTypeOptions = [
  {
    options: trailerEldOptions,
    label: G.getWindowLocale('titles:eld', 'ELD'),
  },
  {
    options: repairShopOptions,
    label: G.getWindowLocale('titles:repair-shop', 'Repair Shop'),
  },
];

const ToggleFuelCard = (props) => {
  const {
    type,
    guid,
    enabled,
    driverGuid,
    getItemListRequest,
    createOrUpdateEntityRequest,
  } = props;

  if (G.notEquals(type, GC.FLEET_INTEGRATION_TYPE_EFS)) return null;

  const enabledIcon = G.ifElse(enabled, I.uiTrue, I.uiFalse)();

  return (
    <Flex width={100} alignItems='baseline' justifyContent='space-around'>
      <Box>{enabledIcon}</Box>
      <AuthWrapper has={[PC.ENABLE_FUEL_CARD_EXECUTE]}>
        <Toggle
          icons={false}
          checked={Boolean(enabled)}
          onChange={() => createOrUpdateEntityRequest({
            method: 'put',
            getItemListRequest,
            groupName: 'integrationList',
            requestPayload: {
              params: { driverGuid },
            },
            endpoint: G.ifElse(
              enabled,
              endpointsMap.fleetDriverExternalDisableByGuid,
              endpointsMap.fleetDriverExternalEnableByGuid,
            )(guid),
          })}
        />
      </AuthWrapper>
    </Flex>
  );
};

const fullOptions = R.flatten([
  eldOptions,
  fuelCardOptions,
  onboardingOptions,
  repairShopOptions,
  trailerEldOptions,
  tollChargeOptions,
  accountingOptions,
]);

const getIntegrationTypeDisplayedValue = (type) => R.compose(
  R.path([GC.FIELD_LABEL]),
  R.find(R.propEq(type, GC.FIELD_VALUE)),
)(fullOptions);

const settings = {
  [GC.FIELD_FLEET_INTEGRATION_TYPE]: {
    width: 150,
    isGrouped: true,
    isRequired: true,
    type: 'reactSelect',
    options: 'integrationOptions',
    name: 'titles:integration-type',
    additionalInputWrapperStyles: { zIndex: 12 },
    customComponent: ({ fieldData }) => getIntegrationTypeDisplayedValue(fieldData),
  },
  [GC.FIELD_FLEET_EXTERNAL_ID]: {
    width: 150,
    isRequired: true,
    name: 'titles:external-id',
    additionalInputWrapperStyles: {
      display: (props) => G.ifElse(
        R.includes(R.path(['values', GC.FIELD_TYPE], props), [...eldIntegrationTypes, ...accountingIntegrationTypes]),
        'flex',
        'none',
      ),
    },
  },
  [GC.FIELD_FLEET_FUEL_CARD_ID]: {
    width: 150,
    isRequired: true,
    name: 'titles:fuel-card-id',
    additionalInputWrapperStyles: {
      display: (props) => G.ifElse(
        R.includes(R.path(['values', GC.FIELD_TYPE], props), fuelCardTypes),
        'flex',
        'none',
      ),
    },
  },
  [GC.FIELD_FLEET_TRANSPONDER_ID]: {
    width: 150,
    isRequired: true,
    name: 'titles:transponder-id',
    additionalInputWrapperStyles: {
      display: (props) => G.ifElse(
        R.includes(R.path(['values', GC.FIELD_TYPE], props), tollChargeTypes),
        'flex',
        'none',
      ),
    },
  },
  [GC.FIELD_TEAM_DRIVER]: {
    width: 200,
    name: 'titles:team-driver-external-id',
    customComponent: (row) => {
      const { lastName, firstName, externalId } = R.pathOr({}, ['data', GC.FIELD_TEAM_DRIVER], row);

      if (G.isNilOrEmpty(externalId)) return null;

      return `${firstName} ${lastName} (${externalId})`;
    },
  },
  [GC.FIELD_TRUCK]: {
    width: 200,
    name: 'titles:truck-external-id',
    customComponent: (row) => {
      const { unitId, externalId } = R.pathOr({}, ['data', GC.FIELD_TRUCK], row);

      if (G.isNilOrEmpty(externalId)) return null;

      return `${unitId} (${externalId})`;
    },
  },
  [GC.FIELD_TRAILERS]: {
    width: 300,
    name: 'titles:trailers-external-id',
    customComponent: (row) => {
      const trailers = R.path(['data', GC.FIELD_TRAILERS], row);

      if (G.isNilOrEmpty(trailers)) return null;

      return R.compose(
        R.join(', '),
        R.map(({ unitId, externalId }) => `${unitId} (${externalId})`),
        R.filter(R.prop(GC.FIELD_FLEET_EXTERNAL_ID)),
      )(trailers);
    },
  },
};

const validationSchema = Yup.lazy(({ type }) => {
  let schema = {
    [GC.FIELD_TYPE]: G.yupStringRequired,
  };

  if (R.includes(type, [...eldIntegrationTypes, ...accountingIntegrationTypes])) {
    schema = R.assoc(GC.FIELD_FLEET_EXTERNAL_ID, G.yupStringRequired, schema);
  }

  if (R.includes(type, fuelCardTypes)) {
    schema = R.assoc(GC.FIELD_FLEET_FUEL_CARD_ID, G.yupStringRequired, schema);
  }

  if (R.includes(type, tollChargeTypes)) {
    schema = R.assoc(GC.FIELD_FLEET_TRANSPONDER_ID, G.yupStringRequired, schema);
  }

  return Yup.object().shape(schema);
});

const defaultInputWrapperStyles = {
  mb: 25,
  width: 300,
};

const defaultValues = R.map(() => null, settings);

const fieldSettings = R.compose(
  R.values,
  R.mapObjIndexed(({
    name,
    options,
    disabled,
    isGrouped,
    type = 'text',
    isRequired = false,
    additionalInputWrapperStyles = {},
  }, fieldName) => ({
    type,
    options,
    disabled,
    isGrouped,
    fieldName,
    isRequired,
    label: G.getWindowLocale(name),
    inputWrapperStyles: R.mergeRight(defaultInputWrapperStyles, additionalInputWrapperStyles),
  })),
  R.pick([
    GC.FIELD_TYPE,
    GC.FIELD_FLEET_EXTERNAL_ID,
    GC.FIELD_FLEET_FUEL_CARD_ID,
    GC.FIELD_FLEET_TRANSPONDER_ID,
  ]),
)(settings);

const makeOptionsForSelect = (itemList, integrationTypeOptions, isTrailer) => {
  const existentIntegrations = R.map(R.prop(GC.FIELD_TYPE), R.or(itemList, []));

  if (isTrailer) {
    const integrationOptions = R.map((item) => R.assoc(
      'isDisabled',
      R.includes(R.prop(GC.FIELD_VALUE, item), existentIntegrations),
      item,
    ), integrationTypeOptions);

    return { integrationOptions };
  }

  const integrationOptions = R.map(({ label, options }) => ({
    label,
    options: R.map((item) => R.assoc(
      'isDisabled',
      R.includes(R.prop(GC.FIELD_VALUE, item), existentIntegrations),
      item,
    ), options),
  }), integrationTypeOptions);

  return { integrationOptions };
};

const getIntegrationIdValue = ({ type, externalId, fuelCardId, transponderId }) => {
  if (R.includes(type, [...eldIntegrationTypes, ...accountingIntegrationTypes])) return externalId;

  if (R.includes(type, fuelCardTypes)) return fuelCardId;

  if (R.includes(type, tollChargeTypes)) return transponderId;

  return '';
};

const columnSettings = {
  [GC.FIELD_FLEET_INTEGRATION_TYPE]: {
    width: 160,
    name: 'titles:integration-type',
    customComponent: ({ fieldData }) => getIntegrationTypeDisplayedValue(fieldData),
    filter: {
      type: 'string',
      getFieldUiValue: ({ type }) => getIntegrationTypeDisplayedValue(type),
    },
  },
  [GC.FIELD_INTEGRATION_ID]: {
    width: 160,
    name: 'titles:integration-id',
    filter: {
      type: 'string',
      getFieldUiValue: getIntegrationIdValue,
    },
    customComponent: ({ data }) => {
      const text = getIntegrationIdValue(data);

      return <TableCell text={text} />;
    },
  },
  [GC.FIELD_ENABLED]: {
    width: 160,
    type: 'boolean',
    filter: { type: 'boolean' },
    name: 'titles:fuel-card-enabled',
    customComponent: ({ data, callbackData }) => (
      <ToggleFuelCard
        {...data}
        {...R.pick(['getItemListRequest', 'createOrUpdateEntityRequest'], callbackData)}
      />
    ),
  },
};

const trailerColumnSettings = {
  ...columnSettings,
  [GC.FIELD_BATTERY_STATUS]: {
    width: 150,
    type: 'enum',
    filter: { type: 'string' },
    name: 'titles:battery-status',
    options: [
      {
        value: ENUMS.ENUM_LOW,
        label: G.getEnumLocale(ENUMS.ENUM_LOW),
      },
      {
        value: ENUMS.ENUM_GOOD,
        label: G.getEnumLocale(ENUMS.ENUM_GOOD),
      },
      {
        value: ENUMS.ENUM_NO_CHARGE,
        label: G.getEnumLocale(ENUMS.ENUM_NO_CHARGE),
      },
    ],
  },
};

const defaultReportFields = R.keys(R.dissoc(GC.FIELD_ENABLED, columnSettings));

const report = {
  [GC.FIELD_TYPE]: GC.TRUCK_PROFILE_UI_REPORTS,
  [GC.FIELD_GUID]: `${GC.TRUCK_PROFILE_UI_REPORTS}Integration`,
  fields: G.mapIndexed((name, sequence) => ({ name, sequence }), defaultReportFields),
};

const trailerReport = {
  [GC.FIELD_TYPE]: GC.TRAILER_PROFILE_UI_REPORTS,
  [GC.FIELD_GUID]: `${GC.TRAILER_PROFILE_UI_REPORTS}Integration`,
  fields: G.mapIndexed(
    (name, sequence) => ({ name, sequence }),
    R.keys(R.dissoc(GC.FIELD_ENABLED, trailerColumnSettings)),
  ),
};

const driverDefaultReportFields = R.keys(columnSettings);

const driverReport = {
  [GC.FIELD_TYPE]: GC.DRIVER_PROFILE_UI_REPORTS,
  [GC.FIELD_GUID]: `${GC.DRIVER_PROFILE_UI_REPORTS}Integration`,
  fields: G.mapIndexed((name, sequence) => ({ name, sequence }), driverDefaultReportFields),
};

const driverIntegrationSettings = {
  defaultValues,
  fieldSettings,
  columnSettings,
  validationSchema,
  report: driverReport,
  useGetListRequest: true,
  withResizableColumns: true,
  groupName: 'integrationList',
  withUiFiltersAndEditView: true,
  hasRemoveRequestOptions: false,
  includeItemListToTablePropsMemoDeps: true,
  defaultReportFields: driverDefaultReportFields,
  additionalTableSettings: { searchableTitles: true },
  itemTitleArr: ['titles:integration', 'integration'],
  formGroupTitleArr: ['titles:integration', 'Integration'],
  tableCallbackDataProps: ['getItemListRequest', 'createOrUpdateEntityRequest'],
  endpoints: {
    remove: 'fleetDriverExternalByGuid',
    createOrUpdate: 'fleetDriverExternal',
    getListByGuid: ({ primaryObjectGuid }) => endpointsMap.fleetDriverExternalByGuid(primaryObjectGuid),
  },
  // helpers
  makeOptionsForSelect: ({ itemList }) => makeOptionsForSelect(itemList, driverIntegrationTypeOptions),
};

const truckIntegrationOptions = {
  ...driverIntegrationSettings,
  report,
  defaultReportFields,
  // helpers
  makeOptionsForSelect: ({ itemList }) => makeOptionsForSelect(itemList, truckIntegrationTypeOptions),
  endpoints: {
    remove: 'truckChangeExternalId',
    createOrUpdate: 'truckCreateExternalId',
    getListByGuid: ({ primaryObjectGuid }) => endpointsMap.truckChangeExternalId(primaryObjectGuid),
  },
};

const trailerIntegrationOptions = {
  ...driverIntegrationSettings,
  report: trailerReport,
  columnSettings: trailerColumnSettings,
  defaultReportFields: [...defaultReportFields, GC.FIELD_BATTERY_STATUS],
  // helpers
  makeOptionsForSelect: ({ itemList }) => makeOptionsForSelect(itemList, trailerIntegrationTypeOptions),
  endpoints: {
    list: 'fleetExternalTrailerList',
    remove: 'trailerChangeExternalId',
    createOrUpdate: 'trailerCreateExternalId',
  },
};

const integrationSettings = {
  groupName: 'integrationList',
  Component: (props) => (
    <AssignmentsIntegrations
      {...props}
      truckSettings={truckIntegrationOptions}
      driverSettings={driverIntegrationSettings}
      trailerSettings={trailerIntegrationOptions}
    />
  ),
};

export {
  // options
  eldOptions,
  fuelCardOptions,
  driverEldOptions,
  repairShopOptions,
  onboardingOptions,
  tollChargeOptions,
  trailerEldOptions,
  accountingOptions,
  driverIntegrationTypeOptions,
  // settings
  integrationSettings,
  truckIntegrationOptions,
  trailerIntegrationOptions,
  // helpers
  getIntegrationTypeDisplayedValue,
};
